import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

export interface SalaryMultiplierTypedAmountConfig {
  typeKey: string;
  /** Sibling holding the dollar amount — the primary `amount` input. */
  dollarKey: string;
  /** Sibling holding the salary multiplier — an extra input. */
  multiplierKey: string;
  /** Sibling holding an extra dollar amount (Guarantee Issue only). */
  additionalKey?: string;
  amountLabel?: string;
  multiplierLabel?: string;
  additionalLabel?: string;
  /**
   * Type options. Set `hideAmount` on any type with no dollar input (a pure
   * salary multiplier, or a sentinel-like value such as "All Guarantee Issue").
   */
  typeOptions: TypedAmountTypeOption[];
  /** Types for which the salary-multiplier input is shown. */
  multiplierTypes: string[];
  /** Types for which the additional-amount input is shown. */
  additionalTypes?: string[];
}

/**
 * Builds a typed-amount spec for a benefit whose value can be a dollar amount,
 * a salary multiplier, or a combination of both — e.g. Basic Life / Voluntary
 * Life benefit amount, maximum, and guarantee issue.
 *
 * The dollar amount is the primary `amount` (hidden per-type via `hideAmount`
 * on the type option); the salary multiplier — and, for Guarantee Issue, an
 * additional amount — are `extraKeys` gated to the relevant types.
 */
export const buildSalaryMultiplierTypedAmount = ({
  typeKey,
  dollarKey,
  multiplierKey,
  additionalKey,
  amountLabel = "Dollar Amount",
  multiplierLabel = "Salary Multiplier",
  additionalLabel = "Additional Amount",
  typeOptions,
  multiplierTypes,
  additionalTypes,
}: SalaryMultiplierTypedAmountConfig): TypedAmountSpec => ({
  typeKey,
  amountKey: dollarKey,
  amountLabel,
  typeOptions,
  extraKeys: [
    {
      key: multiplierKey,
      label: multiplierLabel,
      showForTypes: multiplierTypes,
    },
    ...(additionalKey
      ? [
          {
            key: additionalKey,
            label: additionalLabel,
            showForTypes: additionalTypes ?? [],
          },
        ]
      : []),
  ],
});
